import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  FileText, CheckCircle2, RotateCcw, Lock, Eye, MessageSquare,
} from "lucide-react";

/**
 * Public proof-review page — NO login required. Accessed via a scoped,
 * long-lived token embedded in the proof-ready email. Alternate approvers
 * (marketing team, brand reviewers, etc.) never sign in — they just
 * view, comment, approve, or request revisions on each proof.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const proofTypeLabel = (t) => {
  const s = (t || "").toLowerCase();
  if (s === "graphics") return "Graphics";
  if (s === "rendering" || s === "color_rendering") return "Color Rendering";
  if (s === "signage") return "Signage";
  if (!t) return "Proof";
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
};

export default function ProofReview() {
  const { token } = useParams();
  const [exhibitor, setExhibitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/proof-review/${token}`)
      .then(({ data }) => setExhibitor(data.exhibitor))
      .catch((e) => setError(e?.response?.data?.detail || "This link is no longer valid."))
      .finally(() => setLoading(false));
  }, [token]);

  const refresh = async () => {
    const { data } = await axios.get(`${API}/proof-review/${token}`);
    setExhibitor(data.exhibitor);
  };

  if (loading) {
    return (
      <PageShell>
        <div className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading proof review…</div>
      </PageShell>
    );
  }
  if (error) {
    return (
      <PageShell>
        <div className="max-w-lg">
          <div className="overline">Proof Review</div>
          <h1 className="serif text-3xl md:text-4xl mt-3">Link no longer valid</h1>
          <p className="text-sm text-zinc-600 mt-4 leading-relaxed">{error}</p>
          <p className="text-xs text-zinc-500 mt-4">
            Reach out to Display America at{" "}
            <a href="mailto:info@displayamerica.com" className="underline underline-offset-4">
              info@displayamerica.com
            </a>{" "}
            and we&apos;ll send you a fresh review link.
          </p>
        </div>
      </PageShell>
    );
  }

  const proofs = exhibitor?.proofs || [];
  const rollup = exhibitor?.proof_status;
  const headline = rollup === "approved" ? "All proofs approved."
    : rollup === "revision_requested" ? "Revisions requested."
    : proofs.length === 1 ? "One proof is ready for your review."
    : `${proofs.filter((p) => p.status === "ready").length} of ${proofs.length} proofs awaiting your review.`;

  return (
    <PageShell>
      <Toaster position="top-center" />
      <div className="mb-6">
        <div className="overline">Proof Review · Display America</div>
        <h1 className="serif text-3xl md:text-4xl mt-2" data-testid="proof-review-company">
          {exhibitor.company_name}
        </h1>
        {exhibitor.booth_number && (
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">
            Station {exhibitor.booth_number}
          </div>
        )}
      </div>

      <div className={`border-l-4 pl-4 py-3 mb-8 ${
        rollup === "approved" ? "border-emerald-500 bg-emerald-50/30"
        : rollup === "revision_requested" ? "border-amber-500 bg-amber-50/30"
        : "border-[#A39171] bg-[#A39171]/5"
      }`}>
        <div className="serif text-xl">{headline}</div>
        {proofs.length > 1 && (
          <div className="text-xs text-zinc-500 mt-1">
            Each proof is reviewed independently. Production begins once every proof is approved.
          </div>
        )}
      </div>

      {proofs.length === 0 ? (
        <div className="text-sm text-zinc-500">No proofs uploaded yet.</div>
      ) : (
        <div className="grid gap-5">
          {proofs.map((entry) => (
            <ProofBlock key={entry.id} entry={entry} token={token} refresh={refresh} />
          ))}
        </div>
      )}

      <footer className="mt-12 pt-6 border-t border-zinc-200 text-xs text-zinc-500">
        Questions? Reply to the review email or write to{" "}
        <a href="mailto:info@displayamerica.com" className="underline underline-offset-4">
          info@displayamerica.com
        </a>. Display America, on behalf of Travel + Leisure.
      </footer>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-white text-black">
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="serif text-lg leading-none">TRAVEL + LEISURE</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-0.5">
              Strategic Supplier Summit 2026
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 hidden sm:block">
            Proof Review
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10 md:py-14">{children}</main>
    </div>
  );
}

function ProofBlock({ entry, token, refresh }) {
  const [busy, setBusy] = useState(false);
  const [revising, setRevising] = useState(false);
  const [revisionComment, setRevisionComment] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [note, setNote] = useState("");

  const status = entry.status;
  const fileHref = entry.file_id ? `${BACKEND_URL}/api/files/${entry.file_id}` : null;
  const displayLabel = entry.display_label || entry.filename || "Production proof";

  const call = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await refresh();
      return true;
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Action failed.");
      return false;
    } finally { setBusy(false); }
  };

  const approve = async () => {
    if (!window.confirm(`Approve "${entry.title}"? Once approved this proof is locked and production begins.`)) return;
    await call(
      () => axios.post(`${API}/proof-review/${token}/proofs/${entry.id}/approve`),
      `${entry.title} approved.`,
    );
  };

  const submitRevision = async () => {
    if (revisionComment.trim().length < 5) {
      toast.error("Please describe the changes you need (a few words is enough).");
      return;
    }
    const ok = await call(
      () => axios.post(`${API}/proof-review/${token}/proofs/${entry.id}/revise`,
                        { comment: revisionComment }),
      "Revision request sent to the design team.",
    );
    if (ok) { setRevising(false); setRevisionComment(""); }
  };

  const submitComment = async () => {
    if (note.trim().length < 1) return;
    const ok = await call(
      () => axios.post(`${API}/proof-review/${token}/proofs/${entry.id}/comment`,
                        { comment: note }),
      "Comment sent to the design team.",
    );
    if (ok) { setCommenting(false); setNote(""); }
  };

  const statusPill = status === "approved"
    ? <span className="text-[10px] uppercase tracking-[0.18em] border border-emerald-500 text-emerald-700 bg-emerald-50 px-2 py-1">Approved · Locked</span>
    : status === "revision_requested"
    ? <span className="text-[10px] uppercase tracking-[0.18em] border border-amber-500 text-amber-700 bg-amber-50 px-2 py-1">Revision requested</span>
    : <span className="text-[10px] uppercase tracking-[0.18em] border border-[#A39171] text-[#A39171] bg-[#A39171]/10 px-2 py-1">Awaiting your review</span>;

  return (
    <div className="border border-zinc-200 p-5 md:p-6" data-testid={`proof-review-entry-${entry.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            {proofTypeLabel(entry.proof_type)}
          </div>
          <div className="text-lg font-medium mt-1">{entry.title}</div>
        </div>
        {statusPill}
        {status === "approved" && <Lock size={14} className="text-emerald-700" strokeWidth={1.4} />}
      </div>

      {fileHref && (
        <a href={fileHref} target="_blank" rel="noreferrer"
          className="mt-4 flex items-center gap-3 border border-zinc-200 p-3 hover:border-black transition-colors"
          data-testid={`proof-review-file-${entry.id}`}>
          <FileText size={18} className="text-[#A39171] shrink-0" strokeWidth={1.4} />
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{displayLabel}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Open · New tab</div>
          </div>
          <Eye size={14} className="text-zinc-400 shrink-0" />
        </a>
      )}

      {entry.admin_notes && (
        <div className="mt-4 border-l-2 border-zinc-200 pl-4 text-xs text-zinc-600">
          <div className="uppercase tracking-[0.18em] text-[10px] mb-1">Design notes</div>
          {entry.admin_notes}
        </div>
      )}

      {status === "ready" && !revising && (
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <button onClick={approve} disabled={busy}
            className="flex-1 h-11 px-5 inline-flex items-center justify-center gap-2 bg-black text-white uppercase tracking-[0.18em] text-xs disabled:opacity-40"
            data-testid={`proof-review-approve-${entry.id}`}>
            <CheckCircle2 size={14} /> {busy ? "…" : "Approve"}
          </button>
          <button onClick={() => setRevising(true)} disabled={busy}
            className="flex-1 h-11 px-5 inline-flex items-center justify-center gap-2 border border-black text-black uppercase tracking-[0.18em] text-xs"
            data-testid={`proof-review-revise-${entry.id}`}>
            <RotateCcw size={14} /> Request Revision
          </button>
        </div>
      )}

      {status === "ready" && revising && (
        <div className="mt-5 border-t border-zinc-100 pt-4">
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            What needs to change?
          </label>
          <textarea rows={4} value={revisionComment} onChange={(e) => setRevisionComment(e.target.value)}
            placeholder="Short, specific — the design team will act on it directly."
            data-testid={`proof-review-revision-comment-${entry.id}`}
            className="w-full mt-2 border border-zinc-300 p-3 text-sm focus:outline-none focus:border-black" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            {revisionComment.trim().length}/4000
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button onClick={submitRevision} disabled={busy || revisionComment.trim().length < 5}
              className="flex-1 h-11 bg-black text-white uppercase tracking-[0.18em] text-xs disabled:opacity-40"
              data-testid={`proof-review-revision-submit-${entry.id}`}>
              {busy ? "…" : "Send Revision Request"}
            </button>
            <button onClick={() => { setRevising(false); setRevisionComment(""); }}
              className="h-11 px-4 border border-zinc-300 uppercase tracking-[0.18em] text-xs"
              data-testid={`proof-review-revision-cancel-${entry.id}`}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {status === "approved" && (
        <div className="mt-4 text-xs text-zinc-500" data-testid={`proof-review-approved-${entry.id}`}>
          Locked and forwarded to production on {fmtDateTime(entry.approved_at)}. Thank you.
        </div>
      )}

      {status === "revision_requested" && (
        <div className="mt-4">
          <div className="text-xs text-zinc-500">
            Requested on {fmtDateTime(entry.revision_at)}. The design team will send a revised proof shortly.
          </div>
          {entry.revision_comment && (
            <div className="mt-2 border-l-2 border-amber-500 pl-4 text-xs text-zinc-700">
              <div className="uppercase tracking-[0.18em] text-[10px] mb-1">Your comment</div>
              {entry.revision_comment}
            </div>
          )}
        </div>
      )}

      {/* Free-form comment thread */}
      <div className="mt-5 border-t border-zinc-100 pt-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            Comments ({(entry.supplier_notes || []).length})
          </div>
          {!commenting && status !== "approved" && (
            <button onClick={() => setCommenting(true)}
              className="text-[10px] uppercase tracking-[0.18em] underline underline-offset-4 text-zinc-500 hover:text-black"
              data-testid={`proof-review-add-comment-${entry.id}`}>
              <MessageSquare size={10} className="inline mr-1" /> Add comment
            </button>
          )}
        </div>
        {(entry.supplier_notes || []).length > 0 && (
          <ul className="mt-3 grid gap-2">
            {(entry.supplier_notes || []).map((n) => (
              <li key={n.id} className="text-xs text-zinc-700 border-l-2 border-[#A39171] pl-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  {n.author || "Approver"} · {fmtDateTime(n.at)}
                </div>
                <div className="mt-1 whitespace-pre-wrap">{n.comment}</div>
              </li>
            ))}
          </ul>
        )}
        {commenting && (
          <div className="mt-3">
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note for the design team (question, thought, or clarification)…"
              data-testid={`proof-review-comment-input-${entry.id}`}
              className="w-full border border-zinc-300 p-3 text-sm focus:outline-none focus:border-black" />
            <div className="mt-2 flex gap-2">
              <button onClick={submitComment} disabled={busy || note.trim().length < 1}
                className="h-9 px-4 bg-black text-white uppercase tracking-[0.18em] text-[10px] disabled:opacity-40"
                data-testid={`proof-review-comment-submit-${entry.id}`}>
                {busy ? "…" : "Send"}
              </button>
              <button onClick={() => { setCommenting(false); setNote(""); }}
                className="h-9 px-3 border border-zinc-300 uppercase tracking-[0.18em] text-[10px]"
                data-testid={`proof-review-comment-cancel-${entry.id}`}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
